import React, {
    ReactNode,
    Suspense,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import Spinner from 'component/Layout/Loader/Spinner';

import classNames from 'classnames';
import { useAtomValue } from 'jotai';

import { useOutsideClick } from 'hooks/useOutsideClick';
import { QaIdentifier, useMultipleQa } from 'hooks/useQa';

import { changedOpenTypeAtom } from 'data/Atom/IntegratedOvernight';

import { ReactComponent as IconChevronRounded } from 'media/svg/chevronRounded.svg';

import { lazyRetry } from 'utility/Lazy/Retry';

import { OccupancyValue } from 'types/Search/form';

import { ChangedHighlightType } from '../Form/types';
import Input from '../Input';
import { CustomSearchInputClassList } from '../Input/types';

import {
    convertValueToOverviewData,
    convertValueToUrlParam,
} from './__helper/convert';

import styles from './Occupancy.module.scss';

const Layer = React.lazy(() => lazyRetry(() => import('./__partials/Layer')));

export interface OccupancyProps {
    onValueChange: (value: OccupancyValue) => void;
    customClassList?: CustomSearchInputClassList;
    value: OccupancyValue;
    errorComponent?: ReactNode;
    highlightComponent?: ReactNode;
    label?: string;
    placeholder?: string;
}

const Occupancy = ({
    onValueChange,
    customClassList,
    value,
    errorComponent,
    highlightComponent,
    label = '',
    placeholder = '',
}: OccupancyProps) => {
    const { t } = useTranslation();
    const openType = useAtomValue(changedOpenTypeAtom);
    const isOpen = openType === ChangedHighlightType.TravellerOrRoom;
    const [isLayerOpen, setIsLayerOpen] = useState(false);
    const [qaAttrLayer, qaAttrValue] = useMultipleQa([
        QaIdentifier.Search_Form_Occupancy_Layer,
        QaIdentifier.Search_Form_Occupancy_Value,
    ]);

    useEffect(() => {
        if (isOpen) {
            setIsLayerOpen(isOpen);
        }
    }, [isOpen]);

    const { adultCount, childCount, roomCount, urlParam } = useMemo(() => {
        const overview = convertValueToOverviewData(value);
        const urlParam = convertValueToUrlParam(value);

        return {
            adultCount: overview.adultCount,
            childCount: overview.childAgeList.length,
            roomCount: overview.roomCount,
            urlParam,
        };
    }, [value]);

    const layerRef = useOutsideClick<HTMLDivElement>(() => {
        setIsLayerOpen(false);
    });

    return (
        <div className={styles.Occupancy}>
            <Input
                labelText={label}
                placeholder={placeholder}
                value={t('TRANS-occupancy-detail[i]', {
                    postProcess: 'interval',
                    count: childCount,
                    adultCount,
                    roomCount,
                })}
                callbacks={{
                    onClick: () => {
                        setIsLayerOpen(true);
                    },
                }}
                qaId={QaIdentifier.Search_Form_Occupancy_Value}
                customClassList={{
                    InputWrapper: customClassList?.InputWrapper,
                    Label: customClassList?.Label,
                    Input: classNames(
                        styles.Input,
                        {
                            [styles.Highlight]: !!highlightComponent,
                        },
                        customClassList?.Input
                    ),
                    InputIconWrapper: customClassList?.InputIconWrapper,
                }}
            >
                <input
                    name={'roomAllocation'}
                    type={'hidden'}
                    value={urlParam}
                    id="room-allocation-element"
                    data-param-map="roomAllocation"
                    data-default-searchrequest-value="A-A"
                    {...qaAttrValue}
                />

                <button
                    onClick={() => {
                        setIsLayerOpen(true);
                    }}
                    className={customClassList?.InputIconWrapper}
                >
                    <IconChevronRounded />
                </button>
            </Input>

            {isLayerOpen && (
                <div
                    className={classNames(styles.Layer, customClassList?.Layer)}
                    ref={layerRef}
                    {...qaAttrLayer}
                >
                    <Suspense
                        fallback={
                            <div className={styles.SpinnerWrapper}>
                                <Spinner
                                    classListObject={{
                                        Spinner: styles.Spinner,
                                    }}
                                />
                            </div>
                        }
                    >
                        <Layer
                            onCloseLayer={() => setIsLayerOpen(false)}
                            onValueChange={onValueChange}
                            value={value}
                            errorComponent={errorComponent}
                            highlightComponent={highlightComponent}
                            key={urlParam}
                        />
                    </Suspense>
                </div>
            )}
        </div>
    );
};

export default Occupancy;
